import { Component, OnInit } from '@angular/core';
import {
  ChangeLogEntry,
  ChangeLogType,
} from 'src/app/interfaces/changelog.interface';
import { ApiService } from 'src/app/services/api.service';

@Component({
  standalone: false,
  selector: 'app-changelog',
  templateUrl: './changelog.component.html',
})
export class ChangelogComponent implements OnInit {
  entries: ChangeLogEntry[] = [];
  error = '';
  loading = false;
  selectedType = '';
  playerId = '';
  from = '';
  to = '';
  readonly types: Array<{ value: ChangeLogType; label: string }> = [
    { value: 'SHIP_MOVED', label: 'Schiff bewegt' },
    { value: 'PLANET_COLONIZED', label: 'Planet kolonisiert' },
    { value: 'PLANET_EMPTIED', label: 'Planet leer' },
    { value: 'PLAYER_CREATED', label: 'Spieler erstellt' },
    { value: 'PLAYER_DELETED', label: 'Spieler gelöscht' },
    { value: 'PLAYER_UPDATED', label: 'Spieler geändert' },
  ];

  constructor(private apiService: ApiService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      this.entries = await this.apiService.fetchChangelog({
        type: this.selectedType || undefined,
        playerId: this.playerId ? Number.parseInt(this.playerId, 10) : undefined,
        from: this.from ? new Date(this.from).toISOString() : undefined,
        to: this.to ? new Date(this.to).toISOString() : undefined,
      });
    } catch (e) {
      this.error = e instanceof Error ? e.message : `${e}`;
    } finally {
      this.loading = false;
    }
  }

  reset(): void {
    this.selectedType = '';
    this.playerId = '';
    this.from = '';
    this.to = '';
    void this.load();
  }

  describe(entry: ChangeLogEntry): string {
    switch (entry.type) {
      case 'SHIP_MOVED':
        return `${entry.entityLabel} ist von ${this.coords(entry.fromX, entry.fromY)} nach ${this.coords(entry.toX, entry.toY)} geflogen`;
      case 'PLANET_COLONIZED':
        return `${entry.entityLabel} bei ${this.coords(entry.toX, entry.toY)} wurde kolonisiert`;
      case 'PLANET_EMPTIED':
        return `${entry.entityLabel} bei ${this.coords(entry.toX, entry.toY)} ist jetzt leer`;
      case 'PLAYER_CREATED':
        return `${entry.entityLabel} wurde erstellt`;
      case 'PLAYER_DELETED':
        return `${entry.entityLabel} fehlt in der Siedlerliste`;
      case 'PLAYER_UPDATED':
        return `${entry.entityLabel} wurde geändert`;
      default:
        return entry.type;
    }
  }

  changedValues(entry: ChangeLogEntry): string {
    if (!entry.beforeJson && !entry.afterJson) {
      return '-';
    }
    const before = entry.beforeJson || {};
    const after = entry.afterJson || {};
    const keys = Array.from(
      new Set([...Object.keys(before), ...Object.keys(after)]),
    );
    if (keys.length === 0) {
      return '-';
    }
    return keys
      .map((key) => {
        const beforeValue = this.formatValue(before[key]);
        const afterValue = this.formatValue(after[key]);
        return `${key}: ${beforeValue} -> ${afterValue}`;
      })
      .join(', ');
  }

  typeLabel(type: ChangeLogType): string {
    return this.types.find((entry) => entry.value === type)?.label || type;
  }

  private coords(x?: number, y?: number): string {
    if (x === undefined || y === undefined) {
      return '-';
    }
    return `${x}|${y}`;
  }

  private formatValue(value: unknown): string {
    if (value === undefined || value === null) {
      return '-';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return `${value}`;
  }
}
